import { GameMode, Difficulty } from '../constants.js';
import { SaveManager } from '../world/SaveManager.js';

/**
 * MainMenu - Minecraft-style main menu for FedeWorld
 *
 * Full-screen overlay with animated gradient background, pixelated title,
 * rotating splash text, and stacked menu buttons.
 *
 * Flow: MainMenu -> (optional video intro) -> Game Start
 *
 * API:
 *   const menu = new MainMenu(onStart);
 *   menu.show();
 *   menu.hide();
 *   menu.isOpen  // boolean
 *
 * onStart receives:
 *   { newWorld: false } for "Jugar" (continue saved game)
 *   { newWorld: true, seed: string|null, gameMode: 'survival'|'creative' } for "Nuevo Mundo"
 */

const SPLASH_TEXTS = [
  '\u00A1100% cubos!',
  '\u00A1Hecho por Fede!',
  '\u00A1Ahora con Wither!',
  '\u00A1Pesca incluida!',
  '\u00A1Explora el Nether!',
  '\u00A1Cria animales!',
  '\u00A1Craftea todo!',
  '\u00A1Mina diamantes!',
  '\u00A1Cuidado con los creepers!',
  '\u00A1Construye tu mundo!',
];

export class MainMenu {
  /**
   * @param {function} onStart - Called with options when player starts the game
   */
  constructor(onStart) {
    this.onStart = onStart;
    this.isOpen = false;
    this._animFrame = null;
    this._subPanel = null; // 'newWorld' | 'options' | 'credits' | null

    this._buildOverlay();
    this._buildBackground();
    this._buildTitle();
    this._buildSplash();
    this._buildMainButtons();
    this._buildNewWorldPanel();
    this._buildWorldListPanel();
    this._buildOptionsPanel();
    this._buildCreditsPanel();
    this._buildVersion();

    // Run legacy migration
    SaveManager.migrateIfNeeded();

    // Background video (sunrise loop behind menu)
    this._bgVideo = document.createElement('video');
    this._bgVideo.style.cssText = `
      position: absolute; top: 0; left: 0;
      width: 100%; height: 100%;
      object-fit: cover;
      z-index: 0;
      pointer-events: none;
    `;
    this._bgVideo.loop = true;
    this._bgVideo.muted = true;
    this._bgVideo.playsInline = true;
    this._bgVideo.preload = 'auto';
    // Check if video exists
    fetch('/sunrise.webm', { method: 'HEAD' })
      .then(r => {
        if (r.ok && r.headers.get('content-type')?.startsWith('video/')) {
          this._bgVideo.src = '/sunrise.webm';
          this._bgCanvas.style.opacity = '0.3'; // dim the canvas gradient over video
        }
      })
      .catch(() => {});

    // Assemble into overlay
    this._overlay.appendChild(this._bgVideo);
    this._overlay.appendChild(this._bgCanvas);
    this._overlay.appendChild(this._contentWrap);

    document.body.appendChild(this._overlay);
  }

  // ================================================================
  // Public API
  // ================================================================

  show() {
    this.isOpen = true;
    this._subPanel = null;
    this._showMainButtons();
    this._overlay.style.display = 'flex';
    this._overlay.style.opacity = '1';
    this._startAnimations();
    this._pickSplash();
    if (this._bgVideo && this._bgVideo.src) {
      this._bgVideo.play().catch(() => {});
    }
  }

  hide() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this._stopAnimations();
    if (this._bgVideo) this._bgVideo.pause();

    // Fade out
    this._overlay.style.transition = 'opacity 0.6s ease-out';
    this._overlay.style.opacity = '0';
    setTimeout(() => {
      this._overlay.style.display = 'none';
      this._overlay.style.transition = '';
    }, 700);
  }

  // ================================================================
  // DOM construction
  // ================================================================

  _buildOverlay() {
    this._overlay = document.createElement('div');
    this._overlay.id = 'main-menu-overlay';
    this._overlay.style.cssText = `
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      z-index: 10000;
      display: none;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      cursor: default;
      overflow: hidden;
    `;

    // Content wrapper (sits above canvas)
    this._contentWrap = document.createElement('div');
    this._contentWrap.style.cssText = `
      position: relative;
      z-index: 2;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      padding: 40px 20px;
      box-sizing: border-box;
    `;
  }

  _buildBackground() {
    this._bgCanvas = document.createElement('canvas');
    this._bgCanvas.style.cssText = `
      position: absolute;
      top: 0; left: 0;
      width: 100%; height: 100%;
      z-index: 1;
    `;
  }

  _buildTitle() {
    this._titleEl = document.createElement('div');
    this._titleEl.textContent = 'FEDEWORLD';
    this._titleEl.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 72px;
      font-weight: bold;
      color: #F0C840;
      text-shadow:
        4px 4px 0 #8B6914,
        -2px -2px 0 rgba(0,0,0,0.3),
        0 0 40px rgba(240,200,64,0.3);
      letter-spacing: 8px;
      image-rendering: pixelated;
      margin-bottom: 4px;
      user-select: none;
      text-align: center;
    `;
    this._contentWrap.appendChild(this._titleEl);
  }

  _buildSplash() {
    this._splashWrap = document.createElement('div');
    this._splashWrap.style.cssText = `
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 32px;
    `;

    this._splashEl = document.createElement('div');
    this._splashEl.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 18px;
      font-weight: bold;
      color: #FFFF00;
      text-shadow: 2px 2px 0 rgba(0,0,0,0.5);
      transform: rotate(-8deg);
      user-select: none;
      white-space: nowrap;
    `;

    this._splashWrap.appendChild(this._splashEl);
    this._contentWrap.appendChild(this._splashWrap);
  }

  _buildMainButtons() {
    this._mainBtnsWrap = document.createElement('div');
    this._mainBtnsWrap.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
    `;

    this._btnJugar = this._createButton('Jugar', () => this._onShowWorldList());
    this._btnNuevoMundo = this._createButton('Nuevo Mundo', () => this._onShowNewWorld());
    this._btnOpciones = this._createButton('Opciones', () => this._onShowOptions());
    this._btnCreditos = this._createButton('Creditos', () => this._onShowCredits());

    this._mainBtnsWrap.appendChild(this._btnJugar);
    this._mainBtnsWrap.appendChild(this._btnNuevoMundo);
    this._mainBtnsWrap.appendChild(this._btnOpciones);
    this._mainBtnsWrap.appendChild(this._btnCreditos);

    this._contentWrap.appendChild(this._mainBtnsWrap);
  }

  _buildNewWorldPanel() {
    this._newWorldPanel = document.createElement('div');
    this._newWorldPanel.style.cssText = `
      display: none;
      flex-direction: column;
      align-items: center;
      background: rgba(0, 0, 0, 0.7);
      border: 3px solid;
      border-top-color: #4A4A4A;
      border-left-color: #4A4A4A;
      border-bottom-color: #1A1A1A;
      border-right-color: #1A1A1A;
      padding: 24px 32px;
      min-width: 360px;
      gap: 16px;
    `;

    // Panel title
    const panelTitle = document.createElement('div');
    panelTitle.textContent = 'Nuevo Mundo';
    panelTitle.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 22px;
      font-weight: bold;
      color: #FFFFFF;
      text-shadow: 2px 2px 0 #3F3F3F;
      letter-spacing: 2px;
      margin-bottom: 4px;
    `;
    this._newWorldPanel.appendChild(panelTitle);

    // Seed input
    const seedLabel = document.createElement('div');
    seedLabel.textContent = 'Semilla (opcional):';
    seedLabel.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 14px;
      color: #C0C0C0;
      text-shadow: 1px 1px 0 #3F3F3F;
      align-self: flex-start;
    `;
    this._newWorldPanel.appendChild(seedLabel);

    this._seedInput = document.createElement('input');
    this._seedInput.type = 'text';
    this._seedInput.placeholder = 'Dejar vacio para aleatorio';
    this._seedInput.style.cssText = `
      width: 100%;
      padding: 8px 10px;
      font-family: 'Courier New', monospace;
      font-size: 14px;
      color: #FFFFFF;
      background: #000000;
      border: 2px solid;
      border-color: #373737 #FFFFFF #FFFFFF #373737;
      box-sizing: border-box;
      outline: none;
    `;
    this._seedInput.addEventListener('focus', () => {
      this._seedInput.style.borderColor = '#5A8A30 #8BC34A #8BC34A #5A8A30';
    });
    this._seedInput.addEventListener('blur', () => {
      this._seedInput.style.borderColor = '#373737 #FFFFFF #FFFFFF #373737';
    });
    this._newWorldPanel.appendChild(this._seedInput);

    // Game mode selector
    const modeLabel = document.createElement('div');
    modeLabel.textContent = 'Modo de juego:';
    modeLabel.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 14px;
      color: #C0C0C0;
      text-shadow: 1px 1px 0 #3F3F3F;
      align-self: flex-start;
    `;
    this._newWorldPanel.appendChild(modeLabel);

    // Mode toggle row
    const modeRow = document.createElement('div');
    modeRow.style.cssText = `
      display: flex;
      gap: 12px;
      width: 100%;
      justify-content: center;
    `;

    this._selectedMode = GameMode.SURVIVAL;

    this._btnModeSurvival = this._createModeButton('Supervivencia', GameMode.SURVIVAL);
    this._btnModeCreative = this._createModeButton('Creativo', GameMode.CREATIVE);

    modeRow.appendChild(this._btnModeSurvival);
    modeRow.appendChild(this._btnModeCreative);
    this._newWorldPanel.appendChild(modeRow);
    this._updateModeSelection();

    // Separator
    const sep = document.createElement('div');
    sep.style.cssText = `
      width: 100%;
      height: 2px;
      background: linear-gradient(to right, transparent, #555555, transparent);
      margin: 4px 0;
    `;
    this._newWorldPanel.appendChild(sep);

    // Crear + Atras buttons row
    const btnRow = document.createElement('div');
    btnRow.style.cssText = `
      display: flex;
      gap: 12px;
      width: 100%;
      justify-content: center;
    `;

    this._btnCrear = this._createButton('Crear', () => this._onCrear(), 130);
    this._btnNewWorldBack = this._createButton('Atras', () => this._onBackToMain(), 130);

    btnRow.appendChild(this._btnCrear);
    btnRow.appendChild(this._btnNewWorldBack);
    this._newWorldPanel.appendChild(btnRow);

    this._contentWrap.appendChild(this._newWorldPanel);
  }

  _buildWorldListPanel() {
    this._worldListPanel = document.createElement('div');
    this._worldListPanel.style.cssText = `
      display: none;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      width: 360px;
    `;

    const panelTitle = document.createElement('div');
    panelTitle.textContent = 'Tus Mundos';
    panelTitle.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 20px;
      font-weight: bold;
      color: #FFFFFF;
      text-shadow: 2px 2px 0 #3F3F3F;
      margin-bottom: 10px;
    `;
    this._worldListPanel.appendChild(panelTitle);

    // World slots container
    this._worldSlotsWrap = document.createElement('div');
    this._worldSlotsWrap.style.cssText = 'display:flex;flex-direction:column;gap:6px;width:100%;';
    this._worldListPanel.appendChild(this._worldSlotsWrap);

    // Back button
    const backBtn = this._createButton('Atrás', () => this._onBackToMain(), 130);
    backBtn.style.marginTop = '12px';
    this._worldListPanel.appendChild(backBtn);

    this._contentWrap.appendChild(this._worldListPanel);
  }

  _refreshWorldList() {
    const worlds = SaveManager.getWorldList();
    this._worldSlotsWrap.innerHTML = '';

    for (const w of worlds) {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex;
        align-items: center;
        padding: 8px 12px;
        background: ${w.occupied ? 'rgba(60,60,60,0.8)' : 'rgba(40,40,40,0.5)'};
        border: 2px solid ${w.occupied ? '#555' : '#333'};
        cursor: ${w.occupied ? 'pointer' : 'default'};
        font-family: 'Courier New', monospace;
        gap: 10px;
      `;

      if (w.occupied) {
        const info = document.createElement('div');
        info.style.cssText = 'flex:1;';
        const modeLabel = w.mode === 'creative' ? 'Creativo' : 'Supervivencia';
        const diffLabels = { peaceful: 'Pacífico', easy: 'Fácil', normal: 'Normal', hard: 'Difícil' };
        const diffLabel = diffLabels[w.difficulty] || 'Normal';
        const dateStr = w.lastPlayed ? new Date(w.lastPlayed).toLocaleDateString() : '';
        info.innerHTML = `
          <div style="color:#FFF;font-size:14px;font-weight:bold;text-shadow:1px 1px 0 #222;">${w.name}</div>
          <div style="color:#AAA;font-size:11px;">${modeLabel} — ${diffLabel} ${dateStr ? '— ' + dateStr : ''}</div>
        `;
        row.appendChild(info);

        // Play button
        const playBtn = document.createElement('div');
        playBtn.textContent = '▶';
        playBtn.style.cssText = `
          color: #4CAF50; font-size: 18px; cursor: pointer; padding: 4px 8px;
          border: 1px solid #4CAF50; background: rgba(0,0,0,0.3);
        `;
        playBtn.addEventListener('click', (e) => { e.stopPropagation(); this._onLoadWorld(w.slot); });
        playBtn.addEventListener('mouseenter', () => { playBtn.style.background = 'rgba(76,175,80,0.3)'; });
        playBtn.addEventListener('mouseleave', () => { playBtn.style.background = 'rgba(0,0,0,0.3)'; });
        row.appendChild(playBtn);

        // Delete button
        const delBtn = document.createElement('div');
        delBtn.textContent = '✕';
        delBtn.style.cssText = `
          color: #F44336; font-size: 14px; cursor: pointer; padding: 4px 8px;
          border: 1px solid #F44336; background: rgba(0,0,0,0.3);
        `;
        delBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (confirm(`¿Eliminar "${w.name}"? Esta acción no se puede deshacer.`)) {
            this._onDeleteWorld(w.slot);
          }
        });
        delBtn.addEventListener('mouseenter', () => { delBtn.style.background = 'rgba(244,67,54,0.3)'; });
        delBtn.addEventListener('mouseleave', () => { delBtn.style.background = 'rgba(0,0,0,0.3)'; });
        row.appendChild(delBtn);

        row.addEventListener('click', () => this._onLoadWorld(w.slot));
        row.addEventListener('mouseenter', () => { row.style.background = 'rgba(80,80,80,0.9)'; });
        row.addEventListener('mouseleave', () => { row.style.background = 'rgba(60,60,60,0.8)'; });
      } else {
        const emptyText = document.createElement('div');
        emptyText.textContent = `Slot ${w.slot + 1} — Vacío (clic para crear)`;
        emptyText.style.cssText = 'flex:1;color:#888;font-size:13px;';
        row.appendChild(emptyText);
        row.style.cursor = 'pointer';
        row.addEventListener('click', () => this._onShowNewWorld());
        row.addEventListener('mouseenter', () => { row.style.background = 'rgba(76,175,80,0.2)'; emptyText.style.color = '#AAA'; });
        row.addEventListener('mouseleave', () => { row.style.background = 'rgba(60,60,60,0.8)'; emptyText.style.color = '#888'; });
      }

      this._worldSlotsWrap.appendChild(row);
    }
  }

  _buildOptionsPanel() {
    this._optionsPanel = document.createElement('div');
    this._optionsPanel.style.cssText = `
      display: none;
      flex-direction: column;
      align-items: center;
      background: rgba(0, 0, 0, 0.7);
      border: 3px solid;
      border-top-color: #4A4A4A;
      border-left-color: #4A4A4A;
      border-bottom-color: #1A1A1A;
      border-right-color: #1A1A1A;
      padding: 24px 32px;
      min-width: 360px;
      gap: 16px;
    `;

    const optTitle = document.createElement('div');
    optTitle.textContent = 'Opciones';
    optTitle.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 22px;
      font-weight: bold;
      color: #FFFFFF;
      text-shadow: 2px 2px 0 #3F3F3F;
      letter-spacing: 2px;
    `;
    this._optionsPanel.appendChild(optTitle);

    const comingSoon = document.createElement('div');
    comingSoon.textContent = 'Proximamente';
    comingSoon.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 16px;
      color: #AAAAAA;
      text-shadow: 1px 1px 0 #3F3F3F;
      font-style: italic;
      margin: 16px 0;
    `;
    this._optionsPanel.appendChild(comingSoon);

    const btnBack = this._createButton('Atras', () => this._onBackToMain());
    this._optionsPanel.appendChild(btnBack);

    this._contentWrap.appendChild(this._optionsPanel);
  }

  _buildCreditsPanel() {
    this._creditsPanel = document.createElement('div');
    this._creditsPanel.style.cssText = `
      display: none;
      flex-direction: column;
      align-items: center;
      background: rgba(0, 0, 0, 0.7);
      border: 3px solid;
      border-top-color: #4A4A4A;
      border-left-color: #4A4A4A;
      border-bottom-color: #1A1A1A;
      border-right-color: #1A1A1A;
      padding: 24px 32px;
      min-width: 360px;
      gap: 16px;
    `;

    const credTitle = document.createElement('div');
    credTitle.textContent = 'Creditos';
    credTitle.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 22px;
      font-weight: bold;
      color: #FFFFFF;
      text-shadow: 2px 2px 0 #3F3F3F;
      letter-spacing: 2px;
    `;
    this._creditsPanel.appendChild(credTitle);

    const credText = document.createElement('div');
    credText.textContent = 'Creado por Fede';
    credText.style.cssText = `
      font-family: 'Courier New', monospace;
      font-size: 18px;
      color: #F0C840;
      text-shadow: 2px 2px 0 rgba(0,0,0,0.4);
      margin: 16px 0;
    `;
    this._creditsPanel.appendChild(credText);

    const btnBack = this._createButton('Atras', () => this._onBackToMain());
    this._creditsPanel.appendChild(btnBack);

    this._contentWrap.appendChild(this._creditsPanel);
  }

  _buildVersion() {
    this._versionEl = document.createElement('div');
    this._versionEl.textContent = 'FedeWorld v2.0';
    this._versionEl.style.cssText = `
      position: absolute;
      bottom: 12px;
      left: 16px;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.5);
      text-shadow: 1px 1px 0 rgba(0,0,0,0.5);
      z-index: 3;
      user-select: none;
    `;
    this._overlay.appendChild(this._versionEl);
  }

  // ================================================================
  // Button factories
  // ================================================================

  _createButton(text, onClick, width = 280) {
    const btn = document.createElement('div');
    btn.textContent = text;
    btn.style.cssText = `
      width: ${width}px;
      padding: 8px 0;
      text-align: center;
      font-family: 'Courier New', monospace;
      font-size: 16px;
      font-weight: bold;
      color: #E0E0E0;
      text-shadow: 2px 2px 0 #3F3F3F;
      background: #737373;
      border: 3px solid;
      border-top-color: #A0A0A0;
      border-left-color: #A0A0A0;
      border-bottom-color: #505050;
      border-right-color: #505050;
      cursor: pointer;
      user-select: none;
      image-rendering: pixelated;
      transition: none;
    `;

    btn.addEventListener('mouseenter', () => {
      btn.style.background = '#878787';
      btn.style.color = '#FFFFA0';
      btn.style.borderTopColor = '#B0B0B0';
      btn.style.borderLeftColor = '#B0B0B0';
      btn.style.borderBottomColor = '#606060';
      btn.style.borderRightColor = '#606060';
    });

    btn.addEventListener('mouseleave', () => {
      btn.style.background = '#737373';
      btn.style.color = '#E0E0E0';
      btn.style.borderTopColor = '#A0A0A0';
      btn.style.borderLeftColor = '#A0A0A0';
      btn.style.borderBottomColor = '#505050';
      btn.style.borderRightColor = '#505050';
    });

    btn.addEventListener('mousedown', () => {
      btn.style.borderTopColor = '#505050';
      btn.style.borderLeftColor = '#505050';
      btn.style.borderBottomColor = '#A0A0A0';
      btn.style.borderRightColor = '#A0A0A0';
      btn.style.padding = '9px 0 7px 0';
    });

    btn.addEventListener('mouseup', () => {
      btn.style.borderTopColor = '#A0A0A0';
      btn.style.borderLeftColor = '#A0A0A0';
      btn.style.borderBottomColor = '#505050';
      btn.style.borderRightColor = '#505050';
      btn.style.padding = '8px 0';
    });

    btn.addEventListener('click', onClick);
    return btn;
  }

  _createModeButton(text, mode) {
    const btn = document.createElement('div');
    btn.textContent = text;
    btn.dataset.mode = mode;
    btn.style.cssText = `
      flex: 1;
      padding: 8px 0;
      text-align: center;
      font-family: 'Courier New', monospace;
      font-size: 14px;
      font-weight: bold;
      color: #E0E0E0;
      text-shadow: 2px 2px 0 #3F3F3F;
      background: #737373;
      border: 3px solid;
      border-top-color: #A0A0A0;
      border-left-color: #A0A0A0;
      border-bottom-color: #505050;
      border-right-color: #505050;
      cursor: pointer;
      user-select: none;
      image-rendering: pixelated;
    `;

    btn.addEventListener('mouseenter', () => {
      if (this._selectedMode !== mode) {
        btn.style.background = '#878787';
      }
    });

    btn.addEventListener('mouseleave', () => {
      if (this._selectedMode !== mode) {
        btn.style.background = '#737373';
      }
    });

    btn.addEventListener('click', () => {
      this._selectedMode = mode;
      this._updateModeSelection();
    });

    return btn;
  }

  _updateModeSelection() {
    const buttons = [this._btnModeSurvival, this._btnModeCreative];
    for (const btn of buttons) {
      const isSelected = btn.dataset.mode === this._selectedMode;
      if (isSelected) {
        btn.style.background = '#5A8A30';
        btn.style.color = '#FFFFFF';
        btn.style.borderTopColor = '#7AB050';
        btn.style.borderLeftColor = '#7AB050';
        btn.style.borderBottomColor = '#3A6A10';
        btn.style.borderRightColor = '#3A6A10';
      } else {
        btn.style.background = '#737373';
        btn.style.color = '#E0E0E0';
        btn.style.borderTopColor = '#A0A0A0';
        btn.style.borderLeftColor = '#A0A0A0';
        btn.style.borderBottomColor = '#505050';
        btn.style.borderRightColor = '#505050';
      }
    }
  }

  // ================================================================
  // Panel switching
  // ================================================================

  _showMainButtons() {
    this._mainBtnsWrap.style.display = 'flex';
    this._newWorldPanel.style.display = 'none';
    this._optionsPanel.style.display = 'none';
    this._creditsPanel.style.display = 'none';
    this._splashWrap.style.display = 'flex';
    this._titleEl.style.display = 'block';
    this._subPanel = null;
  }

  _showSubPanel(panelEl, name) {
    this._mainBtnsWrap.style.display = 'none';
    this._newWorldPanel.style.display = 'none';
    this._optionsPanel.style.display = 'none';
    this._creditsPanel.style.display = 'none';
    this._splashWrap.style.display = 'none';
    // Keep title visible
    this._titleEl.style.display = 'block';
    panelEl.style.display = 'flex';
    this._subPanel = name;
  }

  // ================================================================
  // Actions
  // ================================================================

  _onJugar() {
    this.hide();
    this.onStart({ newWorld: false });
  }

  _onShowWorldList() {
    this._refreshWorldList();
    this._showSubPanel(this._worldListPanel, 'worldList');
  }

  _onLoadWorld(slot) {
    this.hide();
    this.onStart({ newWorld: false, slot });
  }

  _onDeleteWorld(slot) {
    SaveManager.deleteWorld(slot);
    this._refreshWorldList();
  }

  _onShowNewWorld() {
    this._seedInput.value = '';
    this._selectedMode = GameMode.SURVIVAL;
    this._updateModeSelection();
    this._showSubPanel(this._newWorldPanel, 'newWorld');
    // Focus seed input after a tick (panel just became visible)
    setTimeout(() => this._seedInput.focus(), 50);
  }

  _onShowOptions() {
    this._showSubPanel(this._optionsPanel, 'options');
  }

  _onShowCredits() {
    this._showSubPanel(this._creditsPanel, 'credits');
  }

  _onBackToMain() {
    this._showMainButtons();
  }

  _onCrear() {
    const seedValue = this._seedInput.value.trim();
    const slot = SaveManager.getNextFreeSlot();
    if (slot < 0) {
      alert('Todos los slots están ocupados. Elimina un mundo primero.');
      return;
    }
    this.hide();
    this.onStart({
      newWorld: true,
      seed: seedValue || null,
      gameMode: this._selectedMode,
      slot,
    });
  }

  // ================================================================
  // Splash text
  // ================================================================

  _pickSplash() {
    const idx = Math.floor(Math.random() * SPLASH_TEXTS.length);
    this._splashEl.textContent = SPLASH_TEXTS[idx];
  }

  // ================================================================
  // Animations
  // ================================================================

  _startAnimations() {
    this._animStart = Date.now();
    this._resizeCanvas();

    // Listen for resize
    this._onResize = () => this._resizeCanvas();
    window.addEventListener('resize', this._onResize);

    const animate = () => {
      if (!this.isOpen) return;
      this._animFrame = requestAnimationFrame(animate);
      this._drawBackground();
      this._animateTitle();
      this._animateSplash();
    };
    this._animFrame = requestAnimationFrame(animate);
  }

  _stopAnimations() {
    if (this._animFrame) {
      cancelAnimationFrame(this._animFrame);
      this._animFrame = null;
    }
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
      this._onResize = null;
    }
  }

  _resizeCanvas() {
    const dpr = window.devicePixelRatio || 1;
    this._bgCanvas.width = window.innerWidth * dpr;
    this._bgCanvas.height = window.innerHeight * dpr;
    this._bgCtx = this._bgCanvas.getContext('2d');
    this._bgCtx.scale(dpr, dpr);
  }

  _drawBackground() {
    const ctx = this._bgCtx;
    if (!ctx) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const t = (Date.now() - this._animStart) / 1000;

    // Animated gradient shifting between warm earth tones and sky blues
    // Use slow sine waves to shift the color stops
    const phase1 = t * 0.15;
    const phase2 = t * 0.1 + 1.5;
    const phase3 = t * 0.12 + 3.0;

    // Color channels oscillating between earth tones and sky tones
    // Top color: sky blue <-> warm amber
    const topR = Math.floor(60 + 80 * (0.5 + 0.5 * Math.sin(phase1)));
    const topG = Math.floor(80 + 80 * (0.5 + 0.5 * Math.sin(phase2)));
    const topB = Math.floor(120 + 80 * (0.5 + 0.5 * Math.sin(phase3)));

    // Middle color: warm earth
    const midR = Math.floor(100 + 60 * (0.5 + 0.5 * Math.sin(phase2 + 1)));
    const midG = Math.floor(70 + 50 * (0.5 + 0.5 * Math.sin(phase1 + 2)));
    const midB = Math.floor(40 + 40 * (0.5 + 0.5 * Math.sin(phase3 + 1)));

    // Bottom color: dark earth / deep blue
    const botR = Math.floor(30 + 40 * (0.5 + 0.5 * Math.sin(phase3 + 2)));
    const botG = Math.floor(25 + 30 * (0.5 + 0.5 * Math.sin(phase1 + 3)));
    const botB = Math.floor(40 + 50 * (0.5 + 0.5 * Math.sin(phase2 + 2)));

    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, `rgb(${topR},${topG},${topB})`);
    grad.addColorStop(0.45, `rgb(${midR},${midG},${midB})`);
    grad.addColorStop(1, `rgb(${botR},${botG},${botB})`);

    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // Subtle dark vignette overlay
    const vigGrad = ctx.createRadialGradient(w / 2, h / 2, h * 0.2, w / 2, h / 2, h * 0.9);
    vigGrad.addColorStop(0, 'rgba(0,0,0,0)');
    vigGrad.addColorStop(1, 'rgba(0,0,0,0.5)');
    ctx.fillStyle = vigGrad;
    ctx.fillRect(0, 0, w, h);
  }

  _animateTitle() {
    const t = (Date.now() - this._animStart) / 1000;
    // Gentle floating: slow up/down movement
    const offsetY = Math.sin(t * 0.8) * 6;
    this._titleEl.style.transform = `translateY(${offsetY}px)`;
  }

  _animateSplash() {
    const t = (Date.now() - this._animStart) / 1000;
    // Pulsing scale on the splash text
    const scale = 1 + 0.05 * Math.sin(t * 3);
    this._splashEl.style.transform = `rotate(-8deg) scale(${scale})`;
  }
}
